/**
 * Bulletin Board Store - Anslagstavla för gruppmedlemmar
 * Hanterar digitala anslag för erbjudanden, efterfrågningar, utlåning och försäljning/byte
 */

import { create } from 'zustand';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { logGroupAction } from '@/lib/activity-logger';
import { useAuthStore } from './auth-store';

// Kategorier för anslagstavla-anslag
export type NoteCategory = 'offer' | 'request' | 'lend' | 'sell' | 'info';

export interface BulletinNote {
  id: string;
  groupId: string;
  authorId: string;
  authorName?: string;
  title: string;
  content: string;
  category: NoteCategory;
  contact?: string;
  createdAt: Date;
  validUntil?: Date;
  isPinned: boolean;
  imageUrl?: string;
  imageBlobName?: string;
}

interface BulletinBoardState {
  // Alla anslag per grupp
  notesByGroup: Record<string, BulletinNote[]>;
  
  // Actions (async to support server persistence)
  fetchNotes: (groupId: string) => Promise<void>;
  addNote: (note: Omit<BulletinNote, 'id' | 'createdAt' | 'isPinned'>) => Promise<void>;
  updateNote: (noteId: string, groupId: string, updates: Partial<Omit<BulletinNote, 'id' | 'groupId' | 'authorId' | 'createdAt'>>) => Promise<void>;
  deleteNote: (noteId: string, groupId: string) => Promise<void>;
  togglePinNote: (noteId: string, groupId: string) => void;
  getNotesForGroup: (groupId: string) => BulletinNote[];
  getNotesByCategory: (groupId: string, category: NoteCategory) => BulletinNote[];
  getMyNotes: (groupId: string, authorId: string) => BulletinNote[];
  searchNotes: (groupId: string, query: string) => BulletinNote[];
}


export const useBulletinBoardStore = create<BulletinBoardState>()((set, get) => ({
      notesByGroup: {},

      fetchNotes: async (groupId: string) => {
        try {
          const res = await apiGet<BulletinNote[]>(`/groups/${groupId}/board`);
          if (res.success && res.data) {
            set((state) => ({ notesByGroup: { ...state.notesByGroup, [groupId]: (res.data as BulletinNote[]).map(d => ({ ...d, createdAt: new Date(d.createdAt) })) } }));
            return;
          }
        } catch (e) {
          // ignore and fallback to persisted state
        }
      },

      addNote: async (note) => {
        try {
          // Premium check: free users can create max 1 note per week, premium users max 50
          const user = useAuthStore.getState().user;
          if (user) {
            const checkRes = await apiGet<{ thisWeekCount: number }>(`/groups/${note.groupId}/board/count-this-week`);
            if (checkRes.success && checkRes.data) {
              const limit = user.subscriptionTier === 'premium' ? 50 : 1;
              if (checkRes.data.thisWeekCount >= limit) {
                const errorMsg = user.subscriptionTier === 'premium' 
                  ? 'Du har nått maxgränsen på 50 anslag per vecka.'
                  : 'Du har nått maxgränsen på 1 anslag per vecka. Uppgradera till Premium för att skapa fler.';
                throw new Error(errorMsg);
              }
            }
          }

          const payload = { ...note, validUntil: note.validUntil ? new Date(note.validUntil).toISOString() : undefined };
          // Do not include authorName in client payload — server will set authoritative display name
          const { authorName: _skip, ...serverPayload } = payload as any;
          console.log('[Bulletin] addNote - sending payload:', serverPayload);
          const res = await apiPost<BulletinNote>(`/groups/${note.groupId}/board`, serverPayload);
          console.log('[Bulletin] addNote - server response:', res);
          if (res.success && res.data) {
            const created = res.data as BulletinNote;
            console.log('[Bulletin] About to log CREATE_BULLETIN_POST activity');
            logGroupAction('CREATE_BULLETIN_POST', note.groupId, { noteId: created.id, title: created.title });
            console.log('[Bulletin] addNote - created note:', created);
            console.log('[Bulletin] addNote - has imageUrl?', created.imageUrl);
            set((state) => {
              const groupNotes = state.notesByGroup[note.groupId] || [];
              return { notesByGroup: { ...state.notesByGroup, [note.groupId]: [...groupNotes, { ...created, createdAt: new Date(created.createdAt) }] } };
            });
            return;
          }
          // Surface server error to caller and console for debugging
          console.error('[Bulletin] addNote failed', res.error || 'Unknown error');
          if (res.error) throw new Error(res.error);
        } catch (e) {
          // fallback: store locally
          const authUser = useAuthStore.getState().user;
          const fallbackAuthorName = authUser ? (authUser.displayName || (authUser as any).name || 'Användare') : 'Användare';
          const newNote: BulletinNote = {
            ...note,
            id: `note-${Date.now()}`,
            createdAt: new Date(),
            isPinned: false,
            authorName: fallbackAuthorName,
          };
          set((state) => {
            const groupNotes = state.notesByGroup[note.groupId] || [];
            return { notesByGroup: { ...state.notesByGroup, [note.groupId]: [...groupNotes, newNote] } };
          });
        }
      },

      updateNote: async (noteId, groupId, updates) => {
        console.log('[Bulletin] updateNote called', { noteId, groupId, updates });
        try {
          const payload = { ...updates } as Record<string, any>;
          if (payload.validUntil && payload.validUntil instanceof Date) payload.validUntil = payload.validUntil.toISOString();
          console.log('[Bulletin] Sending PUT request', { endpoint: `/groups/${groupId}/board/${noteId}`, payload });
          const res = await apiPut<BulletinNote>(`/groups/${groupId}/board/${noteId}`, payload);
          console.log('[Bulletin] PUT response', res);
          if (res.success && res.data) {
            const updated = res.data as BulletinNote;
            console.log('[Bulletin] About to log UPDATE_BULLETIN_POST activity');
            logGroupAction('UPDATE_BULLETIN_POST', groupId, { noteId });
            console.log('[Bulletin] Updated note from server:', updated);
            set((state) => {
              const groupNotes = state.notesByGroup[groupId] || [];
              const newNotes = groupNotes.map(n => {
                if (n.id === noteId) {
                  console.log('[Bulletin] Replacing note:', n, 'with:', updated);
                  return { ...updated, createdAt: new Date(updated.createdAt) };
                }
                return n;
              });
              console.log('[Bulletin] New notes array:', newNotes);
              return { notesByGroup: { ...state.notesByGroup, [groupId]: newNotes } };
            });
            console.log('[Bulletin] Note updated successfully');
            return;
          }
          // If server returned error, throw it
          throw new Error(res.error || 'Failed to update note');
        } catch (e) {
          console.error('[Bulletin] updateNote failed', e);
          // fallback to local update
          set((state) => {
            const groupNotes = state.notesByGroup[groupId] || [];
            return { notesByGroup: { ...state.notesByGroup, [groupId]: groupNotes.map((note) => note.id === noteId ? { ...note, ...updates } : note) } };
          });
          throw e; // Re-throw so caller knows it failed
        }
      },

      deleteNote: async (noteId, groupId) => {
        try {
          const res = await apiDelete(`/groups/${groupId}/board/${noteId}`);
          if (res.success) {
            console.log('[Bulletin] About to log DELETE_BULLETIN_POST activity');
            logGroupAction('DELETE_BULLETIN_POST', groupId, { noteId });
            set((state) => {
              const groupNotes = state.notesByGroup[groupId] || [];
              return { notesByGroup: { ...state.notesByGroup, [groupId]: groupNotes.filter((note) => note.id !== noteId) } };
            });
            return;
          }
        } catch (e) {
          // fallback to local delete
          set((state) => {
            const groupNotes = state.notesByGroup[groupId] || [];
            return { notesByGroup: { ...state.notesByGroup, [groupId]: groupNotes.filter((note) => note.id !== noteId) } };
          });
        }
      },

      togglePinNote: (noteId, groupId) => {
        set((state) => {
          const groupNotes = state.notesByGroup[groupId] || [];
          return {
            notesByGroup: {
              ...state.notesByGroup,
              [groupId]: groupNotes.map((note) =>
                note.id === noteId ? { ...note, isPinned: !note.isPinned } : note
              ),
            },
          };
        });
      },

      getNotesForGroup: (groupId) => {
        const notes = get().notesByGroup[groupId] || [];
        // Sortera: fästa först, sedan nyaste först
        return [...notes].sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
      },

      getNotesByCategory: (groupId, category) => {
        const notes = get().getNotesForGroup(groupId);
        return notes.filter((note) => note.category === category);
      },

      getMyNotes: (groupId, authorId) => {
        const notes = get().getNotesForGroup(groupId);
        return notes.filter((note) => note.authorId === authorId);
      },

      searchNotes: (groupId, query) => {
        const notes = get().getNotesForGroup(groupId);
        const lowerQuery = query.toLowerCase();
        return notes.filter(
          (note) =>
            note.title.toLowerCase().includes(lowerQuery) ||
            note.content.toLowerCase().includes(lowerQuery) ||
            (note.authorName || '').toLowerCase().includes(lowerQuery)
        );
      },
    }));
